import axios from 'axios';
import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { URLS, SHOPIFY_STORES } from '../../config';
import { hooks } from '../../hooks';
import { theme } from '../../constants';
import { components } from '../../components';
import masterBookData from '../../data/books-data.json'; 

export const Library: React.FC = () => {
    const navigate = hooks.useAppNavigate();
    const [purchasedProducts, setPurchasedProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [productsData, setProductsData] = useState<any>([]);
    const API_BASE_URL = 'https://apps.festivehq.com';

    const getFullBookPath = (path: string | undefined): string | undefined => {
        if (!path) return undefined;
        if (path.startsWith('http')) return path;
        if (path.startsWith('/')) return `${API_BASE_URL}${path}`;
        return `${API_BASE_URL}/${path}`;
    };

    const getData = async () => {
        setLoading(true);
        try {
            const products = await axios.get(URLS.GET_PRODUCTS).then(res => res.data.products);
            await axios.get(URLS.GET_BANNERS);
            await axios.get(URLS.GET_CAROUSEL);
            setProductsData(products);
        } catch (error) {
            console.error("Error fetching general data:", error);
        }
    };

    const fetchPurchasedProducts = async () => {         
        const tokenFromLocal = typeof window !== 'undefined' ? localStorage.getItem('customerAccessToken') : null;
        const tokenFromCookie = Cookies.get('customerAccessToken');
        const token = tokenFromLocal || tokenFromCookie;

        if (!token) {
            setLoading(false);
            return;
        }

        // Get the store key the user logged in with
        const storeKey = localStorage.getItem('loggedInStoreKey') || Cookies.get('loggedInStoreKey') || 'USA';
        const storeConfig = SHOPIFY_STORES[storeKey as keyof typeof SHOPIFY_STORES] || SHOPIFY_STORES.USA;


      // Library.tsx: ~Line 60
        const shopifyIdKey = storeKey === 'INDIA' ? 'shopify_product_id_IN' : 'shopify_product_id_US';

        console.log(`[Library Debug] Current Store Key: ${storeKey}`);
        console.log(`[Library Debug] API URL: ${storeConfig.url}`);
        console.log(`[Library Debug] Shopify ID Key for matching: ${shopifyIdKey}`);
        // Ensure token is present, but don't log the full secret token
        console.log(`[Library Debug] Customer Token Present: ${!!token}`);

        const query = `
          query getCustomerOrders($customerAccessToken: String!) {
            customer(customerAccessToken: $customerAccessToken) {
              orders(first: 10, reverse: true) {
                edges {
                  node {
                    lineItems(first: 10) {
                      edges {
                        node {
                          title
                          quantity
                          variant {
                            image { url }
                            price { amount }
                            product { id title }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        try {
            const response = await fetch(storeConfig.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': storeConfig.token,
                },
                body: JSON.stringify({ query, variables: { customerAccessToken: token } }),
            });

            if (!response.ok) {
                const errorBody = await response.text();
                console.error(`[Library Debug] Shopify API Error Status for ${storeKey}: ${response.status}`);
                console.error(`[Library Debug] Shopify API Error Body: ${errorBody.substring(0, 200)}...`); // Log first 200 chars
                // Break out since the request failed
                setLoading(false);
                return;
            }

            const result = await response.json();

            if (result.errors) {
                console.error(`[Library Debug] GraphQL Errors for ${storeKey}:`, result.errors);
            }

            const orders = result?.data?.customer?.orders?.edges || [];
            const products: any[] = [];

            orders.forEach((order: any) => {
                order.node.lineItems.edges.forEach((line: any) => {
                    const p = line.node.variant?.product;
                    if (!p || !p.id) return;
                    const shopifyGlobalId = p.id;
                    let shopifyNumericId: string | undefined;
                    if (typeof shopifyGlobalId === 'string') shopifyNumericId = shopifyGlobalId.split('/').pop();
                    if (!shopifyNumericId) return;

        const matchingBook = masterBookData.find(book => book[shopifyIdKey] === shopifyNumericId);
        if (!matchingBook) {
                        console.warn(`[Library Debug] No matching book found in masterBookData for Shopify ID: ${shopifyNumericId} (Using key: ${shopifyIdKey})`);
                    }

                    if (matchingBook) {
                        if (matchingBook.issues && Array.isArray(matchingBook.issues)) {
                            matchingBook.issues.forEach((issue: any) => {
                                products.push({
                                   id: issue[shopifyIdKey] || matchingBook[shopifyIdKey],
                                    title: issue.issue_title,
                                    image: issue.image_url,
                                    bookPath: getFullBookPath(issue.book_images_json_path),
                                });
                            });
                        } else {
                            products.push({
                               id: matchingBook[shopifyIdKey],
                                title: matchingBook.title,
                                image: matchingBook.image_url,
                                bookPath: getFullBookPath(matchingBook.book_images_json_path),
                            });
                        }
                    }
                });
            });

            console.log(`[Library Debug] Final purchased product count added to state: ${products.length}`);

            setPurchasedProducts(products);
        } catch (err) {
            console.error('Error fetching purchased products:', err);
        } finally {
            setLoading(false);
        }
    };

    // --- New helper to fetch book pages ---
    const fetchBookPages = async (bookPath: string) => {
        try {
            const response = await axios.get(bookPath);
            return response.data; // pages/images JSON
        } catch (err) {
            console.error('Error fetching book pages:', err);
            return null;
        }
    };

    useEffect(() => {
        getData();
        fetchPurchasedProducts();
        window.scrollTo(0, 0);
    }, []);

    const renderHeader = (): JSX.Element => <components.Header line={true} basket={true} />;

    const renderContent = (): JSX.Element => {
        if (loading) return <components.Loader />;

        return (
            <div style={{ padding: '40px 15px 20px 15px', width: '100%', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <div>
                    <h2 style={{ fontSize: 28, textAlign: 'center', fontWeight: 'bold', color: theme.colors.mainColor, margin: '0 0 18px 0', letterSpacing: 1, ...theme.fonts.Mulish_700Bold }}>Library</h2>
                </div>
                <h2 style={{ textAlign: 'center', margin: 0, marginBottom: 20, fontFamily: theme.fonts.Mulish_700Bold.fontFamily, fontWeight: 'bold', fontSize: 22, lineHeight: 1.3, textTransform: 'capitalize', color: theme.colors.mainColor }}>
                    Welcome to Your Library!
                </h2>

                {purchasedProducts.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: 58 }}>
                        {purchasedProducts.map((item, index) => (
                            <div key={item.id}>
                                <button
                                    onClick={async () => {
                                        if (!item.bookPath) return;
                                        const bookPages = await fetchBookPages(item.bookPath); // Fetch pages
                                        if (!bookPages) return;
                                        // Pass pages to BookViewer via state or context
                                        navigate(`/BookViewer/${item.bookPath.split('/').pop()?.replace('.json', '')}`, { state: { pages: bookPages } });
                                    }}
                                    style={{ padding: 0, border: 'none', background: 'none', display: 'block', width: '100%' }}
                                >
                                    <img src={item.image} alt={item.title} style={{ width: '100%', height: 'auto', borderRadius: '12px', objectFit: 'cover', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} />
                                </button>
                                <p style={{ fontSize: 14, marginTop: 8, textAlign: 'center', ...theme.fonts.Mulish_600SemiBold, color: theme.colors.mainColor }}>
                                    {item.title}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ textAlign: 'center', color: theme.colors.textColor }}>You haven't purchased anything yet.</p>
                )}
            </div>
        );
    };

    return (
        <>
            {renderHeader()}
            {renderContent()}
            <components.BottomTabBar />
        </>
    );
};
